import { useEffect, useRef } from 'react';

import { clickAwaySubscribe, clickAwayUnsubscribe } from './useClickAwayEngine';
import { useMemoArray } from './useMemoArray';

import type { ClickAwayEventHandler } from 'shared/types/clickAwayEventHandler';

type ClickAwayConfig = {
  ignoreClassNames?: string[];
  isDisabled?: boolean;
};

const extractElementsFromRef = (ref: React.RefObject<HTMLElement | null> | React.RefObject<HTMLElement | null>[]) => {
  if (!Array.isArray(ref)) {
    return ref.current;
  }
  return ref.map((item) => item.current).filter(Boolean) as HTMLElement[];
};

export const useClickAway = (
  ref: React.RefObject<HTMLElement | null> | React.RefObject<HTMLElement | null>[],
  onClickAway: ClickAwayEventHandler,
  config: ClickAwayConfig = {},
) => {
  const keyRef = useRef<string | null>(null);
  const { ignoreClassNames = [], isDisabled } = config;

  const memoizedIgnoreClassNames = useMemoArray(ignoreClassNames);

  useEffect(() => {
    if (!isDisabled) {
      const bearingElement = extractElementsFromRef(ref);
      if (bearingElement) {
        keyRef.current = clickAwaySubscribe(onClickAway, bearingElement, memoizedIgnoreClassNames);
      }
    } else if (keyRef.current) {
      clickAwayUnsubscribe(keyRef.current);
      keyRef.current = null;
    }

    return () => {
      if (keyRef.current) {
        clickAwayUnsubscribe(keyRef.current);
      }
    };
  }, [ref, onClickAway, isDisabled, memoizedIgnoreClassNames]);
};

export const useRegisterClickAway = (onClickAway: ClickAwayEventHandler, disable: boolean = false) => {
  const ref = useRef<HTMLElement>(undefined);
  const keyRef = useRef<string | null>(null);

  useEffect(() => {
    if (ref.current) {
      if (disable && keyRef.current) {
        clickAwayUnsubscribe(keyRef.current);
        return;
      }
      keyRef.current = clickAwaySubscribe(onClickAway, ref.current);
    }

    return () => {
      if (keyRef.current) {
        clickAwayUnsubscribe(keyRef.current);
      }
    };
  }, [disable, onClickAway]);

  return { ref };
};
